import { createContext, useContext } from "react";

/**
 * Organização na URL: a aplicação vive em `/<slug>/...`. O slug é o primeiro segmento do
 * caminho, desde que não seja uma página de primeiro nível (`/dashboard`, `/platform`…):
 * assim os endereços antigos sem slug continuam a abrir.
 *
 * A base é fixada no arranque (basepath do router). Mudar de organização recarrega a página.
 */

/**
 * O slug é a chave do schema da organização no auth-api (`TenantProvisioningService.CHAVE`):
 * minúsculas, dígitos e underscore, começando por letra ou dígito.
 */
const TENANT_SLUG_MIN_LENGTH = 2;
const TENANT_SLUG_MAX_LENGTH = 56;
const TENANT_SLUG_PATTERN = new RegExp(`^[a-z0-9][a-z0-9_]{${TENANT_SLUG_MIN_LENGTH - 1},${TENANT_SLUG_MAX_LENGTH - 1}}$`);

/** Páginas que pertencem à plataforma ou ao fluxo de entrada, nunca a uma organização. */
export const GLOBAL_ROOTS = ["platform", "convite", "auth", "login", "suporte"] as const;

/** Lê o slug de `pathname`; `pageRoots` são os segmentos de primeiro nível das páginas. */
export function detectTenantSlug(pathname: string, pageRoots: ReadonlySet<string>): string | null {
    const first = pathname.split("/")[1]?.toLowerCase() ?? "";
    if (!first || pageRoots.has(first) || !TENANT_SLUG_PATTERN.test(first)) return null;
    return first;
}

/** Basepath do router: `/<slug>` ou `/`. */
export function tenantBasepath(slug: string | null): string {
    return slug ? `/${slug}` : "/";
}

/** Caminho sem a base da organização (o que o router vê). */
export function stripTenantBase(path: string, slug: string | null): string {
    if (!slug) return path;
    const base = `/${slug}`;
    if (path === base) return "/";
    return path.startsWith(`${base}/`) || path.startsWith(`${base}?`) ? path.slice(base.length) || "/" : path;
}

/** Endereço da raiz de outra organização, para recarregar já dentro dela. */
export function tenantHome(slug: string | null | undefined): string {
    return slug ? `/${slug}/` : "/";
}

export type TenantRouting = {
    /** Organização lida da URL no arranque; `null` nos endereços sem slug. */
    slug: string | null;
    /** Se `/<slug>/` volta a ser lido como organização (e não como página) depois de recarregar. */
    isRoutableSlug: (slug: string) => boolean;
};

/** Fixa a organização da URL no arranque; as páginas de primeiro nível nunca são slug. */
export function createTenantRouting(pathname: string, pageRoots: ReadonlySet<string>): TenantRouting {
    return {
        slug: detectTenantSlug(pathname, pageRoots),
        isRoutableSlug: (slug) => detectTenantSlug(`/${slug}`, pageRoots) === slug,
    };
}

const TenantRoutingContext = createContext<TenantRouting>({ slug: null, isRoutableSlug: () => false });

export const TenantRoutingProvider = TenantRoutingContext.Provider;

export function useTenantRouting(): TenantRouting {
    return useContext(TenantRoutingContext);
}
